// analytics 플러그인 — 화면의 track() 과 useAction 으로 감싼 행동을 모아 POST /api/events 로 보낸다 (prd/103 FR-024)
// 원본은 그 MVP DB 의 events 테이블에만 쌓인다 (INV-005). 지표는 api/metrics/*.sql 로 잰다
import { z } from 'zod';

import { definePlugin, sessionStore } from '../../composition/plugin-runtime';
import { loadAnonymousId } from './adapters/anonymous-id';
import { createHttpEventSink } from './adapters/http-event-sink';
import { createTracker, type Tracker } from './composition/tracker';

// 플러그인 정의는 모듈 하나라 부팅이 여러 번이면(테스트·핫 리로드) 인스턴스가 여럿 생긴다 — 전부 기억해 dispose 에서 정리한다
const instances: { tracker: Tracker; onExit?: () => void }[] = [];

export default definePlugin({
  name: 'analytics',
  version: '0.1.0',
  params: z.object({
    endpoint: z.string().min(1),
    batchSize: z.number().int().min(1).max(100), // 서버 상한 api/internal/events.MaxBatch
    flushIntervalMs: z.number().int().min(100),
    maxAttempts: z.number().int().min(1),
    /** useAction 으로 감싼 행동(complete 단계)을 행동 이름 그대로 남긴다 */
    autoTrackActions: z.boolean(),
    appVersion: z.string().optional(),
  }),
  defaults: {
    // 실패 간격은 5초부터 2배씩(최대 60초) — 5회면 약 75초 오프라인까지 버틴다
    params: { endpoint: '/api/events', batchSize: 20, flushIntervalMs: 5000, maxAttempts: 5, autoTrackActions: true },
  },
  setup(ctx, params) {
    const current = createTracker({
      // 로그인돼 있으면 토큰을 실어 보낸다 — auth 플러그인이 꺼져 있으면 세션이 늘 비어 익명으로 나간다
      sink: createHttpEventSink(params.endpoint, () => sessionStore.get()?.token ?? null),
      anonymousId: loadAnonymousId(() => crypto.randomUUID()),
      batchSize: params.batchSize,
      flushIntervalMs: params.flushIntervalMs,
      maxAttempts: params.maxAttempts,
      appVersion: params.appVersion,
      onDrop: (reason, name) => {
        if (import.meta.env?.DEV) console.warn(`[analytics] ${name} 버림 — ${reason}`);
      },
    });
    const instance: (typeof instances)[number] = { tracker: current };
    instances.push(instance);
    ctx.observe('track', payload => current.enqueue(payload.action, payload.props));
    if (params.autoTrackActions) ctx.observe('complete', payload => current.enqueue(payload.action, {}));

    if (typeof window !== 'undefined') {
      instance.onExit = () => current.flushOnExit();
      window.addEventListener('pagehide', instance.onExit);
    }
  },
  dispose() {
    for (const { tracker, onExit } of instances.splice(0)) {
      tracker.flushOnExit();
      tracker.stop();
      if (onExit && typeof window !== 'undefined') window.removeEventListener('pagehide', onExit);
    }
  },
});
